import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { useSources, sourcesFor } from './sources_context';
import D3CandlestickChart from './components/D3CandlestickChart';

const API_URL = process.env.REACT_APP_API_URL || '';

// ── Design tokens (shared with TrendingV2) ─────────────────────────────────
const T = {
  bgBase: '#080b11', surface1: '#0e131d', surface2: '#0c1118',
  surfaceInset: '#0a0e16', surfaceRaised: '#0b0f17',
  border1: '#1a2230', border2: '#1e2735', borderWarm: '#33301d',
  textHi: '#e7ecf3', textBright: '#fff', textMid: '#c5cdd9',
  textMid2: '#9aa4b2', textMuted: '#8b94a3', textMuted2: '#7f8a9a',
  textFaint: '#6b7585',
  gold: '#fbbf24', green: '#34d399', red: '#f87171',
  indigo: '#818cf8', indigoLight: '#a5b4fc',
  blue: '#7fb0ff', blue2: '#58a6ff',
  cyan: '#5fd6df', orange: '#f0a35e', purple: '#c9a3ff',
  mono: "'JetBrains Mono', ui-monospace, monospace",
};

const BADGE_PALETTE = {
  blue: ['rgba(88,166,255,.14)', '#7cb7ff', 'rgba(88,166,255,.3)'],
  orange: ['rgba(240,136,62,.14)', '#f0a35e', 'rgba(240,136,62,.3)'],
  cyan: ['rgba(86,212,221,.13)', '#5fd6df', 'rgba(86,212,221,.3)'],
  green: ['rgba(52,211,153,.13)', '#46d39c', 'rgba(52,211,153,.3)'],
  red: ['rgba(248,113,113,.13)', '#f48b8b', 'rgba(248,113,113,.3)'],
  purple: ['rgba(188,140,255,.14)', '#c9a3ff', 'rgba(188,140,255,.3)'],
  yellow: ['rgba(227,179,65,.14)', '#e8c46b', 'rgba(227,179,65,.3)'],
  amber: ['rgba(251,191,36,.14)', '#fbcf5b', 'rgba(251,191,36,.32)'],
  gray: ['rgba(139,148,163,.12)', '#9aa4b2', 'rgba(139,148,163,.25)'],
};
function Badge({ icon, label, hue = 'gray', title }) {
  const [bg, fg, bd] = BADGE_PALETTE[hue] || BADGE_PALETTE.gray;
  return (
    <span title={title} style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '4px 8px', borderRadius: 7, background: bg, color: fg,
      border: `1px solid ${bd}`, fontSize: 11.5, fontWeight: 600, whiteSpace: 'nowrap',
    }}>
      <span style={{ fontSize: 12 }}>{icon}</span>{label}
    </span>
  );
}

function CrossSourceIcons({ flags = [true, false, false, false], style = {} }) {
  const items = [
    { icon: '📊', name: 'Pre-Market' },
    { icon: '🐦', name: 'Reddit/WSB' },
    { icon: '💬', name: 'StockTwits' },
    { icon: '🔥', name: 'Trending' },
  ];
  return (
    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, ...style }}>
      {items.map((it, i) => (
        <span key={i} title={`${it.name}: ${flags[i] ? 'flagged' : 'not flagged'}`}
          style={{ fontSize: 13, opacity: flags[i] ? 1 : 0.2, filter: flags[i] ? 'none' : 'grayscale(1)' }}>{it.icon}</span>
      ))}
    </div>
  );
}

function Sparkline({ data, color = T.green, w = 80, h = 24 }) {
  if (!data || data.length < 2) return <svg width={w} height={h} />;
  const min = Math.min(...data), max = Math.max(...data);
  const range = max - min || 1;
  const xStep = w / (data.length - 1);
  const pts = data.map((v, i) => `${(i * xStep).toFixed(1)},${(h - ((v - min) / range) * h).toFixed(1)}`).join(' ');
  const lastX = (data.length - 1) * xStep;
  const lastY = h - ((data[data.length - 1] - min) / range) * h;
  return (
    <svg width={w} height={h} style={{ display: 'block' }}>
      <polyline points={pts} fill="none" stroke={color} strokeWidth="1.5" />
      <circle cx={lastX} cy={lastY} r="2" fill={color} />
    </svg>
  );
}

// Determine which category a ticker belongs to (for display)
function CategoryChip({ cat }) {
  const colors = {
    small_cap_gappers: { bg: 'rgba(88,166,255,.13)', fg: '#7cb7ff', bd: 'rgba(88,166,255,.3)', label: 'Small cap' },
    large_cap_gappers: { bg: 'rgba(188,140,255,.13)', fg: '#c9a3ff', bd: 'rgba(188,140,255,.3)', label: 'Large cap' },
    gap_downs: { bg: 'rgba(248,113,113,.13)', fg: '#f48b8b', bd: 'rgba(248,113,113,.3)', label: 'Gap down' },
    low_float_runners: { bg: 'rgba(240,136,62,.13)', fg: '#f0a35e', bd: 'rgba(240,136,62,.3)', label: 'Low float' },
    ai_ranked: { bg: 'rgba(240,180,0,.13)', fg: '#f0b400', bd: 'rgba(240,180,0,.3)', label: 'AI ranked' },
    penny_stock_movers: { bg: 'rgba(227,179,65,.13)', fg: '#e8c46b', bd: 'rgba(227,179,65,.3)', label: 'Penny' },
  };
  const c = colors[cat] || colors.small_cap_gappers;
  return (
    <span style={{
      padding: '3px 8px', borderRadius: 6, background: c.bg, color: c.fg,
      border: `1px solid ${c.bd}`, fontSize: 10.5, fontWeight: 700, letterSpacing: '.03em',
    }}>{c.label}</span>
  );
}

function badgesForTicker(t) {
  const out = [];
  const gap = parseFloat(t.gap_pct || 0);
  const vol = t.volume || 0;
  const avgVol = t.avg_volume || 0;
  const relVol = avgVol > 0 ? vol / avgVol : null;
  if (gap >= 5) out.push({ icon: '⚡', label: `Gap +${gap.toFixed(1)}%`, hue: 'yellow', title: 'Pre-market gap' });
  else if (gap <= -5) out.push({ icon: '⚡', label: `Gap ${gap.toFixed(1)}%`, hue: 'red', title: 'Pre-market gap down' });
  if (relVol && relVol >= 2) out.push({ icon: '🔥', label: `Rel vol ${relVol.toFixed(1)}×`, hue: 'amber', title: 'Volume vs 20-day avg' });
  if (t.has_news) out.push({ icon: '📰', label: `${t.news_count || 1} news`, hue: 'blue', title: t.latest_headline?.slice(0, 100) || 'Recent news' });
  const w52h = t['52wk_high'];
  const price = t.price;
  if (w52h && price && price >= w52h * 0.95 && price <= w52h * 1.05) out.push({ icon: '📊', label: '52w high', hue: 'green', title: 'Near 52-week high' });
  const w52l = t['52wk_low'];
  if (w52l && price && price <= w52l * 1.05) out.push({ icon: '📊', label: '52w low', hue: 'cyan', title: 'Near 52-week low' });
  return out;
}

// ── Hero card (top setup) ─────────────────────────────────────────────────
function PreMarketHero({ ticker, category, isStrong, rank }) {
  const gap = parseFloat(ticker.gap_pct || 0);
  const vol = ticker.volume || 0;
  const avgVol = ticker.avg_volume || 0;
  const relVol = avgVol > 0 ? vol / avgVol : null;
  const badges = badgesForTicker(ticker);
  return (
    <div style={{
      background: isStrong ? 'linear-gradient(160deg,#13110a,#0d1119)' : T.surface1,
      border: `1px solid ${isStrong ? T.borderWarm : T.border2}`,
      borderRadius: 16, padding: 18,
      boxShadow: isStrong ? '0 0 0 1px rgba(251,191,36,.25), 0 0 20px -8px rgba(251,191,36,.3)' : 'none',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        {rank ? (
          <span style={{ fontFamily: T.mono, fontWeight: 700, fontSize: 12, color: rank <= 3 ? T.gold : T.textMid,
            background: rank <= 3 ? 'rgba(251,191,36,.09)' : 'transparent', border: `1px solid ${rank <= 3 ? 'rgba(251,191,36,.28)' : T.border1}`,
            borderRadius: 5, padding: '1px 7px' }}>#{rank}</span>
        ) : null}
        <span style={{ fontFamily: T.mono, fontSize: 20, fontWeight: 700, color: T.textBright }}>{ticker.symbol}</span>
        <CategoryChip cat={category} />
        <CrossSourceIcons flags={ticker._cross || [true, false, false, false]} style={{ marginLeft: 'auto' }} />
      </div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 14, marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 10, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>Gap</div>
          <div style={{ fontFamily: T.mono, fontSize: 22, fontWeight: 700, color: gap >= 0 ? T.green : T.red, marginTop: 2 }}>
            {gap >= 0 ? '+' : ''}{gap.toFixed(1)}%
          </div>
        </div>
        <div>
          <div style={{ fontSize: 10, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>Price</div>
          <div style={{ fontFamily: T.mono, fontSize: 17, fontWeight: 600, color: T.textHi, marginTop: 2 }}>
            ${(ticker.price || 0).toFixed(2)}
          </div>
        </div>
        <div style={{ marginLeft: 'auto', textAlign: 'right' }}>
          <div style={{ fontSize: 10, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>Pre-mkt vol</div>
          <div style={{ fontFamily: T.mono, fontSize: 13, fontWeight: 600, color: T.textMid, marginTop: 2 }}>
            {(vol / 1e6).toFixed(2)}M
            {relVol && relVol >= 1.5 && <span style={{ color: T.gold, marginLeft: 6 }}>· {relVol.toFixed(1)}×</span>}
          </div>
        </div>
      </div>
      {ticker.has_news && ticker.latest_headline && (
        <div style={{
          padding: '8px 10px', borderRadius: 8,
          background: 'rgba(88,166,255,.06)', border: '1px solid rgba(88,166,255,.18)',
          fontSize: 11.5, color: T.textMid, marginBottom: 10, lineHeight: 1.4,
        }}>
          📰 {ticker.latest_headline.slice(0, 110)}{ticker.latest_headline.length > 110 ? '…' : ''}
        </div>
      )}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
        {badges.map((b, i) => <Badge key={i} {...b} />)}
      </div>
    </div>
  );
}

// ── Gap distribution horizontal bar chart ─────────────────────────────────
function GapDistributionChart({ allTickers }) {
  const buckets = [
    { label: '<−5%', range: [-Infinity, -5], color: T.red },
    { label: '−5 to −2%', range: [-5, -2], color: '#f8a05b' },
    { label: '−2 to 2%', range: [-2, 2], color: T.textMuted },
    { label: '2 to 5%', range: [2, 5], color: '#5e9e7e' },
    { label: '5 to 10%', range: [5, 10], color: T.green },
    { label: '10 to 20%', range: [10, 20], color: T.gold },
    { label: '>20%', range: [20, Infinity], color: '#f59e0b' },
  ];
  buckets.forEach((b) => { b.count = 0; });
  allTickers.forEach((t) => {
    const g = parseFloat(t.gap_pct || 0);
    for (const b of buckets) { if (g >= b.range[0] && g < b.range[1]) { b.count++; break; } }
  });
  const max = Math.max(1, ...buckets.map((b) => b.count));
  return (
    <div style={{
      background: T.surface1, border: `1px solid ${T.border2}`, borderRadius: 16, padding: 16,
    }}>
      <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>
        Gap distribution
      </div>
      {buckets.map((b, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 7 }}>
          <span style={{ fontSize: 10.5, color: T.textMuted2, width: 80, textAlign: 'right', fontFamily: T.mono }}>{b.label}</span>
          <div style={{ flex: 1, background: T.surfaceInset, borderRadius: 6, height: 14, position: 'relative', overflow: 'hidden' }}>
            <div style={{ width: `${(b.count / max) * 100}%`, background: b.color, height: '100%', borderRadius: 6, transition: 'width .3s' }} />
            {b.count > 0 && (
              <span style={{
                position: 'absolute', left: 7, top: '50%', transform: 'translateY(-50%)',
                fontFamily: T.mono, fontSize: 10, fontWeight: 700, color: '#fff', textShadow: '0 0 4px rgba(0,0,0,.6)',
              }}>{b.count}</span>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Category donut ─────────────────────────────────────────────────────────
function CategoryDonut({ summary, total }) {
  const items = [
    { key: 'small_caps', label: 'Small cap', color: '#7fb0ff' },
    { key: 'large_caps', label: 'Large cap', color: '#c9a3ff' },
    { key: 'gap_downs', label: 'Gap down', color: '#f48b8b' },
    { key: 'penny_stocks', label: 'Penny', color: '#e8c46b' },
  ];
  const counts = items.map((it) => ({ ...it, n: summary?.[it.key] || 0 }));
  const sum = counts.reduce((a, b) => a + b.n, 0) || 1;
  let acc = 0;
  const gradStops = counts.map((c) => {
    const start = acc;
    acc += (c.n / sum) * 100;
    return `${c.color} ${start.toFixed(2)}% ${acc.toFixed(2)}%`;
  }).join(', ');
  return (
    <div style={{
      background: T.surface1, border: `1px solid ${T.border2}`, borderRadius: 16, padding: 16,
      display: 'flex', alignItems: 'center', gap: 14,
    }}>
      <div style={{
        position: 'relative', width: 112, height: 112, borderRadius: '50%',
        background: `conic-gradient(${gradStops})`,
        flexShrink: 0,
      }}>
        <div style={{
          position: 'absolute', inset: 18, borderRadius: '50%', background: T.surface1,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
        }}>
          <span style={{ fontFamily: T.mono, fontSize: 18, fontWeight: 700, color: T.textBright }}>{total || sum}</span>
          <span style={{ fontSize: 9, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>scanned</span>
        </div>
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 5 }}>
        {counts.map((c) => (
          <div key={c.key} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ width: 9, height: 9, borderRadius: 2, background: c.color }} />
            <span style={{ fontSize: 12, color: T.textMid }}>{c.label}</span>
            <span style={{ marginLeft: 'auto', fontFamily: T.mono, fontSize: 12, fontWeight: 600, color: T.textHi }}>{c.n}</span>
            <span style={{ fontFamily: T.mono, fontSize: 11, color: T.textMuted2, width: 38, textAlign: 'right' }}>
              {((c.n / sum) * 100).toFixed(0)}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── News modal (click any ticker's news cell) ──────────────────────────────
function NewsModal({ symbol, onClose }) {
  const [items, setItems] = useState(null);
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => { window.removeEventListener('keydown', onKey); document.body.style.overflow = ''; };
  }, [onClose]);
  useEffect(() => {
    let dead = false;
    (async () => {
      const out = [];
      try {
        const g = await axios.get(`${API_URL}/api/gdelt-news/${symbol}`);
        (g.data.articles || []).forEach((a) => out.push({
          time: a.time, source: a.source, headline: a.headline, url: a.url, tone: a.tone, via: 'GDELT',
        }));
      } catch (e) {}
      if (!dead) setItems([...out]);
      try {
        const r = await axios.get(`${API_URL}/api/news/${symbol}`);
        const live = r.data.news || r.data.articles || [];
        const seen = new Set(out.map((x) => (x.headline || '').toLowerCase().slice(0, 60)));
        live.forEach((a) => {
          const h = a.headline || a.title || '';
          if (!h || seen.has(h.toLowerCase().slice(0, 60))) return;
          out.push({
            time: a.datetime || a.published || a.time || '', source: a.source, headline: h,
            summary: a.summary, url: a.url || a.link, via: 'live',
          });
        });
      } catch (e) {}
      if (!dead) setItems([...out]);
    })();
    return () => { dead = true; };
  }, [symbol]);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(4,6,10,.78)', backdropFilter: 'blur(3px)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18 }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: T.surface1, border: `1px solid ${T.border2}`, borderRadius: 18, padding: 22, width: 'min(760px, 95vw)', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 24px 80px rgba(0,0,0,.6)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
          <span style={{ fontFamily: T.mono, fontSize: 24, fontWeight: 800, color: T.textBright }}>{symbol}</span>
          <span style={{ fontSize: 12, color: T.textMuted2 }}>news · GDELT (48h) + Yahoo/Finnhub live</span>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: T.surface2, border: `1px solid ${T.border2}`, color: T.textMid, borderRadius: 9, padding: '6px 12px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>✕</button>
        </div>
        {items === null ? (
          <div style={{ padding: 30, textAlign: 'center', color: T.textMuted }}>Loading news…</div>
        ) : items.length === 0 ? (
          <div style={{ padding: 30, textAlign: 'center', color: T.textMuted }}>No recent articles found for {symbol}.</div>
        ) : (
          items.map((a, i) => (
            <a key={i} href={a.url} target="_blank" rel="noreferrer" style={{ display: 'block', textDecoration: 'none', padding: '11px 4px', borderBottom: `1px solid ${T.border1}` }}>
              <div style={{ display: 'flex', gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
                <span style={{ fontFamily: T.mono, fontSize: 10.5, color: T.textFaint }}>{a.time}</span>
                <span style={{ fontSize: 10.5, color: T.cyan, fontWeight: 600 }}>{a.source}</span>
                {a.tone != null && (
                  <span style={{ fontFamily: T.mono, fontSize: 10, fontWeight: 700, color: a.tone >= 0 ? '#34d399' : '#f87171' }}>
                    {a.tone >= 0 ? '▲' : '▼'} {Math.abs(a.tone).toFixed(1)}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 13.5, color: '#dbe3ee', marginTop: 3, lineHeight: 1.45 }}>{a.headline}</div>
              {a.summary && <div style={{ fontSize: 11.5, color: T.textMuted, marginTop: 3, lineHeight: 1.45 }}>{String(a.summary).slice(0, 220)}</div>}
            </a>
          ))
        )}
      </div>
    </div>
  );
}

// ── Side dock: legacy-style stacked cards (chart / news / details) ─────────
function MiniLineChart({ candles, w = 330, h = 150 }) {
  if (!candles || candles.length < 2) return null;
  const pts = candles.map((c) => ({ t: c.datetime, v: c.close }));
  const vals = pts.map((p) => p.v);
  const min = Math.min(...vals), max = Math.max(...vals), range = max - min || 1;
  const padY = 10, padB = 14;
  const X = (i) => i * (w / (pts.length - 1));
  const Y = (v) => padY + (h - padY - padB) * (1 - (v - min) / range);
  const line = pts.map((p, i) => `${i ? 'L' : 'M'}${X(i).toFixed(1)},${Y(p.v).toFixed(1)}`).join(' ');
  const up = vals[vals.length - 1] >= vals[0];
  const col = up ? '#34d399' : '#f87171';
  const fmt = (t) => new Date(t).toTimeString().slice(0, 5);
  const ticks = [0, Math.floor((pts.length - 1) / 2), pts.length - 1];
  return (
    <svg viewBox={`0 0 ${w} ${h}`} style={{ display: 'block', width: '100%', height: 'auto' }}>
      <defs><linearGradient id="dockg" x1="0" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor={col} stopOpacity="0.28" /><stop offset="100%" stopColor={col} stopOpacity="0" />
      </linearGradient></defs>
      <path d={`${line} L${w},${h - padB} L0,${h - padB} Z`} fill="url(#dockg)" />
      <path d={line} fill="none" stroke={col} strokeWidth="1.8" />
      <circle cx={X(pts.length - 1)} cy={Y(vals[vals.length - 1])} r="2.5" fill={col} />
      {ticks.map((i, k) => (
        <text key={k} x={Math.min(Math.max(X(i), 16), w - 18)} y={h - 2} fontSize="9" fill="#7f8a9a" textAnchor="middle" fontFamily="monospace">{fmt(pts[i].t)}</text>
      ))}
    </svg>
  );
}

function DockCard({ icon, title, onClose, children }) {
  return (
    <div style={{ margin: '10px 12px 0', background: T.surface2, border: `1px solid ${T.border1}`, borderRadius: 14, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 13px', borderBottom: `1px solid ${T.border1}` }}>
        <span style={{ fontSize: 13 }}>{icon}</span>
        <span style={{ fontSize: 12.5, fontWeight: 700, color: T.textHi }}>{title}</span>
        <button onClick={onClose} style={{ marginLeft: 'auto', background: 'transparent', border: 'none', color: T.textMuted, fontSize: 14, cursor: 'pointer', padding: 2 }}>✕</button>
      </div>
      {children}
    </div>
  );
}

function ChartCardBody({ symbol, row, onEmpty }) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const RANGES = [['full', 'Full'], ['rth', '9:30+'], ['1h', '1H'], ['30m', '30M']];
  const [range, setRange] = useState('full');
  useEffect(() => {
    let r = 'full';
    try { r = localStorage.getItem('mf_chart_range_' + symbol) || 'full'; } catch (e) {}
    setRange(r);
  }, [symbol]);
  const pickRange = (r) => { setRange(r); try { localStorage.setItem('mf_chart_range_' + symbol, r); } catch (e) {} };
  const chartData = useMemo(() => {
    const all = (data && data.candles) || [];
    if (all.length < 2) return data;
    const lastT = all[all.length - 1].datetime;
    const em = (t) => { const p = new Date(t).toLocaleTimeString('en-GB', { timeZone: 'America/New_York', hour12: false }).split(':'); return (+p[0]) * 60 + (+p[1]); };
    const inR = (c) => range === 'rth' ? em(c.datetime) >= 570
      : range === '1h' ? c.datetime >= lastT - 3600000
      : range === '30m' ? c.datetime >= lastT - 1800000 : true;
    const shown = all.filter(inR);
    return { ...data, candles: shown.length >= 2 ? shown : all };
  }, [data, range]);
  useEffect(() => {
    if (!data) return;
    const cs = data.candles || [];
    if (cs.length < 2 && onEmpty) onEmpty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);
  useEffect(() => {
    let dead = false;
    setData(null); setErr(null);
    axios.get(`${API_URL}/api/chart/${symbol}`)
      .then((r) => { if (!dead) setData(r.data); })
      .catch((e) => { if (!dead) setErr(e.message); });
    return () => { dead = true; };
  }, [symbol]);
  if (err) return <div style={{ padding: 14, color: T.red, fontSize: 12 }}>{err}</div>;
  if (!data) return <div style={{ padding: 14, color: T.textMuted, fontSize: 12 }}>Loading intraday…</div>;
  const cs = data.candles || [];
  if (cs.length < 2) return <div style={{ padding: 14, color: T.textMuted, fontSize: 12 }}>No intraday prints yet.</div>;
  const last = cs[cs.length - 1].close;
  const etMin = (t) => {
    const p = new Date(t).toLocaleTimeString('en-GB', { timeZone: 'America/New_York', hour12: false }).split(':');
    return (+p[0]) * 60 + (+p[1]);
  };
  const regular = cs.filter((c) => etMin(c.datetime) >= 570);  // >= 9:30 ET
  const marketOpen = regular.length >= 1;
  const prev = (row && row.prev_close) || cs[0].open;
  let refLabel, refVal, hi, lo, ch, chLabel;
  if (marketOpen) {
    const opn = (row && row.open_price) || regular[0].open;
    hi = Math.max(...regular.map((c) => c.high));
    lo = Math.min(...regular.map((c) => c.low));
    refLabel = 'Open'; refVal = opn;
    ch = opn > 0 ? (last / opn - 1) * 100 : 0;
    chLabel = 'from open';
  } else {
    hi = Math.max(...cs.map((c) => c.high));
    lo = Math.min(...cs.map((c) => c.low));
    refLabel = 'Prev'; refVal = prev;
    ch = prev > 0 ? (last / prev - 1) * 100 : 0;
    chLabel = 'premkt';
  }
  return (
    <div style={{ padding: '10px 13px 13px' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, whiteSpace: 'nowrap', fontFamily: T.mono, fontSize: 12 }}>
        <span style={{ fontSize: 17, fontWeight: 800, color: T.textBright }}>${last.toFixed(2)}</span>
        <span style={{ color: T.textMuted }}>{refLabel}: <b style={{ color: T.textMid }}>${(refVal || 0).toFixed(2)}</b></span>
        <span style={{ marginLeft: 'auto', fontWeight: 800, color: ch >= 0 ? T.green : T.red }}>
          {ch >= 0 ? '+' : ''}{ch.toFixed(1)}% <span style={{ fontSize: 10, color: T.textMuted, fontWeight: 600 }}>{chLabel}</span>
        </span>
      </div>
      <div style={{ display: 'flex', gap: 5, marginTop: 9, marginBottom: 2 }}>
        {RANGES.map(([k, lbl]) => (
          <button key={k} onClick={() => pickRange(k)} style={{
            fontFamily: T.mono, fontSize: 10.5, fontWeight: 700, padding: '3px 9px', borderRadius: 6,
            cursor: 'pointer', border: `1px solid ${range === k ? 'rgba(251,191,36,.4)' : T.border2}`,
            background: range === k ? 'rgba(251,191,36,.1)' : T.surface2, color: range === k ? T.gold : T.textMuted,
          }}>{lbl}</button>
        ))}
      </div>
      <div style={{ marginTop: 6 }}><D3CandlestickChart chartData={chartData} /></div>
      <div style={{ fontSize: 9.5, color: T.textFaint, marginTop: 4 }}>pick a range above (saved per ticker) · drag a box to zoom · double-click to reset</div>
    </div>
  );
}

const _newsCache = new Map();  // symbol -> { at, items }

function NewsCardBody({ symbol, onEmpty }) {
  const [items, setItems] = useState(null);
  const [finished, setFinished] = useState(false);
  useEffect(() => {
    if (finished && items && items.length === 0 && onEmpty) onEmpty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finished, items]);
  useEffect(() => {
    let dead = false;
    const hit = _newsCache.get(symbol);
    if (hit && Date.now() - hit.at < 5 * 60 * 1000) {
      setItems(hit.items);
      setFinished(true);
      return undefined;
    }
    setItems(null);
    (async () => {
      const out = [];
      try {
        const g = await axios.get(`${API_URL}/api/gdelt-news/${symbol}`);
        (g.data.articles || []).forEach((a) => out.push({ time: a.time, source: a.source, headline: a.headline, url: a.url, tone: a.tone }));
      } catch (e) {}
      if (!dead) setItems([...out]);
      try {
        const r = await axios.get(`${API_URL}/api/news/${symbol}`);
        const live = r.data.news || [];
        const seen = new Set(out.map((x) => (x.headline || '').toLowerCase().slice(0, 60)));
        live.forEach((a) => {
          const h = a.headline || a.title || '';
          if (!h || seen.has(h.toLowerCase().slice(0, 60))) return;
          out.push({ time: a.datetime || a.published || a.time || '', source: a.source, headline: h, url: a.url || a.link, summary: a.summary });
        });
      } catch (e) {}
      if (!dead) { setItems([...out]); setFinished(true); _newsCache.set(symbol, { at: Date.now(), items: [...out] }); }
    })();
    return () => { dead = true; };
  }, [symbol]);
  if (items === null) return <div style={{ padding: 14, color: T.textMuted, fontSize: 12 }}>Loading news…</div>;
  if (items.length === 0) return <div style={{ padding: 14, color: T.textMuted, fontSize: 12 }}>No recent articles found.</div>;
  return (
    <div style={{ padding: '4px 10px 12px', maxHeight: 420, overflowY: 'auto' }}>
      {items.map((a, i) => (
        <a key={i} href={a.url} target="_blank" rel="noreferrer" style={{ display: 'block', textDecoration: 'none', margin: '8px 2px 0', padding: '9px 11px', borderRadius: 10, background: T.surfaceInset, border: `1px solid ${T.border1}` }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ fontSize: 10.5, color: T.blue, fontWeight: 700 }}>{a.source}</span>
            <span style={{ fontFamily: T.mono, fontSize: 10.5, color: T.textMuted, whiteSpace: 'nowrap' }}>
              {(() => {
                const raw = String(a.time || '').trim().replace(' ', 'T');
                if (!raw) return '—';
                const d = new Date(raw + (raw.length === 16 ? ':00Z' : raw.endsWith('Z') ? '' : 'Z'));
                if (isNaN(d.getTime())) return a.time;
                return d.toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hour12: false }) + ' ET';
              })()}{a.tone != null ? (a.tone >= 0 ? ' ▲' : ' ▼') : ''}
            </span>
          </div>
          <div style={{ fontSize: 12.5, color: a.tone != null ? (a.tone >= 0 ? '#7ee2b8' : '#f2a3a3') : '#dbe3ee', marginTop: 4, lineHeight: 1.45 }}>{a.headline}</div>
        </a>
      ))}
    </div>
  );
}

function DetailsCardBody({ row }) {
  if (!row) return <div style={{ padding: 14, color: T.textMuted, fontSize: 12 }}>Not in current scan.</div>;
  const relVol = row.avg_volume > 0 ? (row.volume || 0) / row.avg_volume : null;
  const opn = row.open_price || 0;
  const fromOpen = opn > 0 && row.price > 0 ? (row.price / opn - 1) * 100 : null;
  const spread = (row.bid != null && row.ask != null && row.ask >= row.bid && (row.price || 0) > 0) ? (row.ask - row.bid) / row.price * 100 : null;
  const fmtV = (v) => v >= 1e6 ? `${(v / 1e6).toFixed(2)}M` : v > 0 ? v.toLocaleString() : '—';
  const fmtC = (x) => x >= 1e9 ? `$${(x / 1e9).toFixed(2)}B` : x >= 1e6 ? `$${(x / 1e6).toFixed(2)}M` : x > 0 ? `$${Math.round(x / 1e3)}K` : '—';
  const rsi = row.rsi != null ? Math.round(parseFloat(row.rsi)) : null;
  const rows = [
    ['Symbol', row.symbol, T.textHi],
    ['Price', `$${(row.price || 0).toFixed(2)}`, T.textHi],
    ['Gap %', `${row.gap_pct >= 0 ? '+' : ''}${(row.gap_pct || 0).toFixed(2)}%`, row.gap_pct >= 0 ? T.green : T.red],
    ['% From Open', fromOpen == null ? '—' : `${fromOpen >= 0 ? '+' : ''}${fromOpen.toFixed(2)}%`, fromOpen == null ? T.textMuted : fromOpen >= 0 ? T.green : T.red],
    ['Volume', fmtV(row.volume || 0), T.textHi],
    ['Relative Volume', relVol ? `${relVol.toFixed(1)}x` : '—', relVol >= 2 ? T.gold : T.textHi],
    ['ATR', row.atr != null ? parseFloat(row.atr).toFixed(2) : '—', T.textHi],
    ['RSI', rsi == null ? '—' : rsi, rsi == null ? T.textMuted : rsi >= 70 ? T.red : rsi <= 30 ? T.green : T.textHi],
    ['Spread %', spread != null ? `${spread.toFixed(2)}%` : '—', spread > 2 ? T.red : T.textHi],
    ['Float', row.float_shares ? fmtV(row.float_shares) : '—', row.float_shares && row.float_shares < 20e6 ? T.gold : T.textHi],
    ['Market Cap', fmtC(row.market_cap || 0), T.textHi],
    ['52w Range', row['52wk_low'] ? `$${row['52wk_low'].toFixed(2)} – $${(row['52wk_high'] || 0).toFixed(2)}` : '—', T.textHi],
  ];
  return (
    <div style={{ padding: '4px 13px 12px' }}>
      {rows.map(([l, v, c], i) => (
        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '7px 0', borderBottom: i < rows.length - 1 ? `1px solid ${T.border1}` : 'none' }}>
          <span style={{ fontSize: 12, color: T.textMuted }}>{l}</span>
          <span style={{ fontFamily: T.mono, fontSize: 12.5, fontWeight: 700, color: c }}>{v}</span>
        </div>
      ))}
    </div>
  );
}

function SideDock({ symbol, row, panels, onClose, onCloseAll }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onCloseAll(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCloseAll]);
  if (!symbol || (!panels.chart && !panels.news && !panels.details)) return null;
  return (
    <div className="pm-dock" style={{ position: 'fixed', top: 62, right: 0, bottom: 0, width: 392, maxWidth: '94vw', zIndex: 70, background: T.surface1, borderLeft: `1px solid ${T.border2}`, boxShadow: '-16px 0 40px rgba(0,0,0,.45)', overflowY: 'auto', paddingBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', padding: '13px 14px 4px' }}>
        <span style={{ fontFamily: T.mono, fontSize: 20, fontWeight: 800, color: T.textBright }}>{symbol}</span>
        <button onClick={onCloseAll} style={{ marginLeft: 'auto', background: T.surface2, border: `1px solid ${T.border2}`, color: T.textMid, borderRadius: 9, padding: '5px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Close all ✕</button>
      </div>
      {panels.chart && (
        <DockCard icon="📈" title={`${symbol} Intraday Chart`} onClose={() => onClose('chart')}>
          <ChartCardBody symbol={symbol} row={row} onEmpty={() => onClose('chart')} />
        </DockCard>
      )}
      {panels.news && (
        <DockCard icon="📰" title={`${symbol} News`} onClose={() => onClose('news')}>
          <NewsCardBody symbol={symbol} onEmpty={() => onClose('news')} />
        </DockCard>
      )}
      {panels.details && (
        <DockCard icon="📋" title="Stock Details" onClose={() => onClose('details')}>
          <DetailsCardBody row={row} />
        </DockCard>
      )}
    </div>
  );
}

function TopMoversModal({ data, onClose, onOpenNews, onRerun, loading }) {
  if (!data) return null;
  const w = data.watchlist || [];
  const convColor = (c) => (c === 'high' ? T.green : c === 'medium' ? T.gold : T.textMuted);
  const biasChip = (b) => {
    const col = b === 'long' ? T.green : b === 'short' ? T.red : T.textMuted;
    return <span style={{ fontFamily: T.mono, fontSize: 10, fontWeight: 800, color: col, border: `1px solid ${col}55`, borderRadius: 5, padding: '1px 6px', textTransform: 'uppercase' }}>{b}</span>;
  };
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(4,6,10,.78)', backdropFilter: 'blur(3px)', zIndex: 120, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 18px', overflowY: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(920px, 96vw)', background: T.surface1, border: `1px solid ${T.border2}`, borderRadius: 18, boxShadow: '0 30px 90px rgba(0,0,0,.6)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 22px', borderBottom: `1px solid ${T.border2}` }}>
          <span style={{ fontSize: 18 }}>🎯</span>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: T.textBright }}>Top Movers — AI Day-Trade Watchlist</div>
            <div style={{ fontSize: 11.5, color: T.textMuted, marginTop: 2 }}>
              {data.totalFiltered} candidates matched the tier filter · Claude ranked the best {w.length} to watch
            </div>
          </div>
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8 }}>
            {data.cached && <span style={{ fontSize: 10.5, color: T.textMuted, fontFamily: T.mono }}>cached{data.cached_at ? ' ' + new Date(data.cached_at).toLocaleTimeString('en-US', { timeZone: 'America/New_York', hour: '2-digit', minute: '2-digit' }) + ' ET' : ''}</span>}
            <button onClick={onRerun} disabled={loading} style={{ background: T.surface2, border: '1px solid rgba(129,140,248,.4)', color: T.indigoLight, borderRadius: 9, padding: '5px 12px', fontSize: 12.5, fontWeight: 700, cursor: loading ? 'wait' : 'pointer', fontFamily: 'inherit' }}>{loading ? '⏳ Re-running…' : '🔄 Re-run AI'}</button>
            <button onClick={onClose} style={{ background: T.surface2, border: `1px solid ${T.border2}`, color: T.textMid, borderRadius: 9, padding: '5px 12px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Close ✕</button>
          </div>
        </div>
        <div style={{ padding: '16px 22px 22px' }}>
          {data.error && <div style={{ color: T.red, fontSize: 13, padding: 12 }}>{data.error}</div>}
          {data.llm_error && <div style={{ color: T.gold, fontSize: 12, marginBottom: 12 }}>AI ranking unavailable ({data.llm_error}); showing tier-filtered list below.</div>}
          <div style={{ fontSize: 11, fontWeight: 700, color: T.gold, letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 10 }}>⭐ Best to day-trade today</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 22 }}>
            {w.map((p) => (
              <div key={p.symbol} style={{ display: 'grid', gridTemplateColumns: '30px 82px 1fr auto', alignItems: 'center', gap: 12, padding: '11px 14px', borderRadius: 12, background: T.surface2, border: `1px solid ${p.conviction === 'high' ? 'rgba(52,211,153,.28)' : T.border1}` }}>
                <span style={{ fontFamily: T.mono, fontWeight: 800, color: T.indigoLight, fontSize: 13 }}>#{p.rank}</span>
                <div>
                  <div style={{ fontFamily: T.mono, fontWeight: 800, color: T.textBright, fontSize: 15 }}>{p.symbol}</div>
                  <div style={{ fontFamily: T.mono, fontSize: 10, color: T.textMuted }}>T{p.tier} · ${p.price}</div>
                </div>
                <div>
                  <div style={{ fontSize: 12.5, color: '#dbe3ee', lineHeight: 1.4 }}>{p.reason}</div>
                  {p.explanation && <div style={{ fontSize: 11.5, color: T.textMuted, lineHeight: 1.5, marginTop: 4 }}>{p.explanation}</div>}
                  <div style={{ display: 'flex', gap: 10, marginTop: 5, fontFamily: T.mono, fontSize: 10.5, color: T.textMuted, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span style={{ color: (p.gap_pct >= 0 ? T.green : T.red) }}>gap {p.gap_pct >= 0 ? '+' : ''}{p.gap_pct}%</span>
                    {p.rel_vol != null && <span>relVol {p.rel_vol}x</span>}
                    {p.rsi != null && <span>RSI {p.rsi}</span>}
                    {p.float_shares ? <span>float {(p.float_shares / 1e6).toFixed(1)}M</span> : null}
                    {p.news_count ? <span onClick={() => onOpenNews && onOpenNews(p.symbol)} style={{ color: T.blue, cursor: 'pointer' }}>📰 {p.news_count}</span> : null}
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5 }}>
                  <span style={{ fontSize: 10.5, fontWeight: 800, color: convColor(p.conviction), textTransform: 'uppercase' }}>{p.conviction}</span>
                  {biasChip(p.bias)}
                </div>
              </div>
            ))}
            {!w.length && !data.error && <div style={{ color: T.textMuted, fontSize: 12 }}>No qualifying momentum names in this scan.</div>}
          </div>
          <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted2, letterSpacing: '.05em', textTransform: 'uppercase', marginBottom: 8 }}>
            All tier matches ({data.totalFiltered})
          </div>
          <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 10, lineHeight: 1.5 }}>
            <b style={{ color: T.green }}>T1</b>: $1–10 · has news · gap ≥5% · vol &gt;100K &nbsp;·&nbsp; <b style={{ color: T.gold }}>T2</b>: $1–10 · gap ≥3% · vol &gt;100K
          </div>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead><tr style={{ color: T.textMuted2, textAlign: 'left' }}>
                {['#', 'Tier', 'Symbol', 'Price', 'Gap %', 'Vol', 'RelVol', 'RSI', 'News', 'Headline'].map((h) => (
                  <th key={h} style={{ padding: '6px 8px', fontWeight: 700, borderBottom: `1px solid ${T.border2}`, whiteSpace: 'nowrap' }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {(data.topMovers || []).map((s2, i) => (
                  <tr key={s2.symbol} style={{ borderBottom: `1px solid ${T.border1}` }}>
                    <td style={{ padding: '6px 8px', fontFamily: T.mono, color: T.textMuted }}>{i + 1}</td>
                    <td style={{ padding: '6px 8px', fontWeight: 700, color: s2.tier === 1 ? T.green : T.gold }}>T{s2.tier}</td>
                    <td style={{ padding: '6px 8px', fontFamily: T.mono, fontWeight: 700, color: T.textHi }}>{s2.symbol}</td>
                    <td style={{ padding: '6px 8px', fontFamily: T.mono }}>${s2.price}</td>
                    <td style={{ padding: '6px 8px', fontFamily: T.mono, color: s2.gap_pct >= 0 ? T.green : T.red }}>{s2.gap_pct >= 0 ? '+' : ''}{s2.gap_pct}%</td>
                    <td style={{ padding: '6px 8px', fontFamily: T.mono, color: T.textMid }}>{s2.volume >= 1e6 ? (s2.volume / 1e6).toFixed(1) + 'M' : Math.round(s2.volume / 1e3) + 'K'}</td>
                    <td style={{ padding: '6px 8px', fontFamily: T.mono, color: T.textMid }}>{s2.rel_vol != null ? s2.rel_vol + 'x' : '—'}</td>
                    <td style={{ padding: '6px 8px', fontFamily: T.mono, color: T.textMid }}>{s2.rsi != null ? s2.rsi : '—'}</td>
                    <td style={{ padding: '6px 8px', fontFamily: T.mono, color: s2.news_count ? T.blue : T.textFaint }}>{s2.news_count || 0}</td>
                    <td style={{ padding: '6px 8px', color: T.textMuted, maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s2.latest_headline || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

function StreamModal({ info, allTickers, status, sel, setSel, onClose, onStart, onStop, onAnalyze }) {
  // Build the row list: prefer the AI watchlist (ranked + explained); fall back to
  // top up-gappers from the scan if Analyze hasn't been run yet.
  const ranked = Object.values(info || {}).filter((x) => x && x.symbol && x.rank).sort((a, b) => a.rank - b.rank);
  const dedupFallback = (() => {
    const bySym = {};
    for (const t of (allTickers || [])) {
      if (!((t.gap_pct || 0) >= 3 && (t.volume || 0) > 100000 && (t.price || 0) >= 1 && (t.price || 0) <= 20)) continue;
      const sym = (t.symbol || '').toUpperCase();
      if (!sym) continue;
      if (!bySym[sym] || (t.volume || 0) > (bySym[sym]._vol || 0)) {
        bySym[sym] = { symbol: sym, gap_pct: t.gap_pct, price: t.price, rel_vol: (t.avg_volume > 0 ? (t.volume || 0) / t.avg_volume : null), reason: t.latest_headline || '', _vol: t.volume || 0 };
      }
    }
    return Object.values(bySym).sort((a, b) => (b.gap_pct || 0) - (a.gap_pct || 0)).slice(0, 40);
  })();
  const rows = ranked.length ? ranked : dedupFallback;
  const liveBy = {};
  (allTickers || []).forEach((t) => {
    const sy = (t.symbol || '').toUpperCase();
    if (sy && (!liveBy[sy] || (t.volume || 0) > (liveBy[sy].volume || 0))) liveBy[sy] = t;
  });
  const streaming = new Set((status.tickers || []).map((x) => x.toUpperCase()));
  const selSet = new Set(sel.map((x) => x.toUpperCase()));
  const toggle = (sym) => setSel((prev) => {
    const S = new Set(prev.map((x) => x.toUpperCase()));
    if (S.has(sym)) S.delete(sym); else S.add(sym);
    return Array.from(S);
  });
  const convColor = (c) => (c === 'high' ? T.green : c === 'medium' ? T.gold : T.textMuted);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(4,6,10,.78)', backdropFilter: 'blur(3px)', zIndex: 130, display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 18px', overflowY: 'auto' }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: 'min(880px, 96vw)', background: T.surface1, border: `1px solid ${T.border2}`, borderRadius: 18, boxShadow: '0 30px 90px rgba(0,0,0,.6)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '18px 22px', borderBottom: `1px solid ${T.border2}` }}>
          <span style={{ fontSize: 18 }}>📡</span>
          <div>
            <div style={{ fontSize: 16, fontWeight: 800, color: T.textBright }}>Live Tick Streaming</div>
            <div style={{ fontSize: 11.5, color: T.textMuted, marginTop: 2 }}>
              {status.running
                ? <>🔴 Streaming <b style={{ color: T.red }}>{(status.tickers || []).length}</b> tickers · {status.rows || 0} ticks saved today</>
                : <>Pick tickers to record ~every 2s for backtesting · auto-stops at 6pm ET</>}
            </div>
          </div>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: T.surface2, border: `1px solid ${T.border2}`, color: T.textMid, borderRadius: 9, padding: '5px 12px', fontSize: 13, cursor: 'pointer', fontFamily: 'inherit' }}>Close ✕</button>
        </div>
        <div style={{ padding: '14px 22px 6px', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          {!ranked.length && (
            <button onClick={onAnalyze} style={{ padding: '7px 13px', borderRadius: 9, border: '1px solid rgba(251,191,36,.4)', background: 'linear-gradient(180deg,#221c11,#181208)', color: T.gold, fontSize: 12.5, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>🎯 Run Analyze for ranked picks</button>
          )}
          <button onClick={() => setSel(rows.slice(0, 10).map((r) => r.symbol))} style={{ padding: '6px 11px', borderRadius: 8, border: `1px solid ${T.border2}`, background: T.surface2, color: T.textMid, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Select top 10</button>
          <button onClick={() => setSel([])} style={{ padding: '6px 11px', borderRadius: 8, border: `1px solid ${T.border2}`, background: T.surface2, color: T.textMid, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>Clear</button>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: T.textMuted, fontFamily: T.mono }}>{sel.length} selected</span>
        </div>
        <div style={{ padding: '6px 16px 12px', maxHeight: '52vh', overflowY: 'auto' }}>
          {rows.length === 0 ? (
            <div style={{ padding: 20, color: T.textMuted, fontSize: 13, textAlign: 'center' }}>No candidates — run a scan or Analyze Top Movers first.</div>
          ) : rows.map((r, i) => {
            const sym = (r.symbol || '').toUpperCase();
            const on = selSet.has(sym);
            return (
              <div key={sym + i} onClick={() => toggle(sym)} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 11px', margin: '5px 0', borderRadius: 10, cursor: 'pointer', background: on ? 'rgba(52,211,153,.08)' : T.surface2, border: `1px solid ${on ? 'rgba(52,211,153,.35)' : T.border1}` }}>
                <span style={{ width: 18, height: 18, borderRadius: 5, border: `1px solid ${on ? T.green : T.border2}`, background: on ? T.green : 'transparent', color: '#0b0e16', fontSize: 12, fontWeight: 900, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{on ? '✓' : ''}</span>
                {r.rank ? <span style={{ fontFamily: T.mono, fontWeight: 800, fontSize: 12, color: T.indigoLight, minWidth: 22 }}>#{r.rank}</span> : null}
                <div style={{ minWidth: 66 }}>
                  <span style={{ fontFamily: T.mono, fontWeight: 800, fontSize: 14, color: T.textBright }}>{sym}</span>
                  {streaming.has(sym) && <span style={{ marginLeft: 6, width: 7, height: 7, borderRadius: '50%', background: T.red, display: 'inline-block', boxShadow: `0 0 5px ${T.red}` }} />}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: T.textMid, lineHeight: 1.4, overflow: 'hidden', textOverflow: 'ellipsis' }}>{r.reason || '—'}</div>
                  {(() => {
                    const lt = liveBy[sym] || {};
                    const px = lt.price != null ? lt.price : r.price;
                    const vol = lt.volume != null ? lt.volume : null;
                    const atr = lt.atr != null ? parseFloat(lt.atr) : (r.atr != null ? parseFloat(r.atr) : null);
                    const opn = lt.open_price || 0;
                    const openPct = opn > 0 && px > 0 ? (px / opn - 1) * 100 : null;
                    const age = lt.news_age_hours;
                    const newsState = lt.has_news ? 'fresh' : (age != null && age <= 24 * 45 ? 'old' : 'none');
                    return (
                      <div style={{ display: 'flex', gap: 10, marginTop: 3, fontFamily: T.mono, fontSize: 10.5, color: T.textMuted, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span style={{ color: (r.gap_pct >= 0 ? T.green : T.red) }}>gap {r.gap_pct >= 0 ? '+' : ''}{(r.gap_pct || 0).toFixed ? r.gap_pct.toFixed(1) : r.gap_pct}%</span>
                        {openPct != null && <span style={{ color: openPct >= 0 ? T.green : T.red, fontWeight: 700 }}>opn {openPct >= 0 ? '+' : ''}{openPct.toFixed(1)}%</span>}
                        {px != null && <span style={{ color: T.textHi }}>${typeof px === 'number' ? px.toFixed(2) : px}</span>}
                        {vol != null && <span>vol {vol >= 1e6 ? (vol / 1e6).toFixed(1) + 'M' : Math.round(vol / 1e3) + 'K'}</span>}
                        {r.rel_vol != null && <span>relVol {typeof r.rel_vol === 'number' ? r.rel_vol.toFixed(1) : r.rel_vol}x</span>}
                        {atr != null && <span>ATR ${atr.toFixed(2)}</span>}
                        <span title={newsState === 'fresh' ? `${lt.news_count || 1} recent articles` : newsState === 'old' ? `news ~${Math.round(age / 24)}d old` : 'no news'}
                          style={{ opacity: newsState === 'fresh' ? 1 : newsState === 'old' ? 0.5 : 0.18, filter: newsState === 'none' ? 'grayscale(1)' : 'none' }}>
                          📰{newsState === 'fresh' ? lt.news_count || '' : ''}
                        </span>
                      </div>
                    );
                  })()}
                </div>
                {r.conviction && <span style={{ fontSize: 10, fontWeight: 800, color: convColor(r.conviction), textTransform: 'uppercase' }}>{r.conviction}</span>}
              </div>
            );
          })}
        </div>
        <div style={{ display: 'flex', gap: 10, padding: '14px 22px 20px', borderTop: `1px solid ${T.border2}` }}>
          <button onClick={() => onStart(sel)} disabled={sel.length === 0} style={{ flex: 1, padding: '12px', borderRadius: 11, border: 'none', background: sel.length ? 'linear-gradient(180deg,#12351f,#0d2417)' : T.surface2, color: sel.length ? T.green : T.textFaint, fontSize: 14, fontWeight: 800, cursor: sel.length ? 'pointer' : 'default', fontFamily: 'inherit', border: `1px solid ${sel.length ? 'rgba(52,211,153,.4)' : T.border2}` }}>
            {status.running ? `Update stream → ${sel.length} tickers` : `▶ Start streaming ${sel.length} tickers`}
          </button>
          {status.running && (
            <button onClick={onStop} style={{ padding: '12px 18px', borderRadius: 11, border: '1px solid rgba(248,113,113,.4)', background: 'rgba(248,113,113,.1)', color: T.red, fontSize: 14, fontWeight: 800, cursor: 'pointer', fontFamily: 'inherit' }}>■ Stop all</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main Pre-Market Tab ────────────────────────────────────────────────────
export default function PreMarketV2() {
  const sources = useSources();
  const [scans, setScans] = useState([]);
  const [selectedScanId, setSelectedScanId] = useState(null);
  const [atmData, setAtmData] = useState(null);
  const [atmLoading, setAtmLoading] = useState(false);
  const [atmRanks, setAtmRanks] = useState({});  // symbol -> rank
  const [atmInfo, setAtmInfo] = useState({});    // symbol -> {rank,reason,explanation,conviction,bias,tier}
  const [streamStatus, setStreamStatus] = useState({ running: false, tickers: [], rows: 0 });
  const [streamOpen, setStreamOpen] = useState(false);
  const [streamSel, setStreamSel] = useState([]);
  const streamSet = new Set((streamStatus.running ? (streamStatus.tickers || []) : []).map((x) => x.toUpperCase()));
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      if (stop) return;
      try { const r = await axios.get(`${API_URL}/api/stream/status`); if (!stop) setStreamStatus(r.data || { running: false, tickers: [] }); } catch (e) {}
    };
    tick();
    const iv = setInterval(tick, 5000);
    return () => { stop = true; clearInterval(iv); };
  }, []);
  const startStream = async (tickers) => {
    try {
      const r = await axios.post(`${API_URL}/api/stream/start`, { tickers });
      setStreamStatus((p) => ({ ...p, running: true, tickers }));
      setTimeout(async () => { try { const s2 = await axios.get(`${API_URL}/api/stream/status`); setStreamStatus(s2.data); } catch (e) {} }, 800);
    } catch (e) {}
  };
  const stopStream = async () => {
    try { await axios.post(`${API_URL}/api/stream/stop`); setStreamStatus({ running: false, tickers: [], rows: 0 }); } catch (e) {}
  };
  const removeStreamTicker = (sym) => {
    const next = (streamStatus.tickers || []).filter((x) => x.toUpperCase() !== sym.toUpperCase());
    if (next.length) startStream(next); else stopStream();
  };
  const openStreamModal = () => {
    setStreamSel(Array.from(streamSet));
    setStreamOpen(true);
  };
  const [alerts, setAlerts] = useState([]);
  const [notifyOn, setNotifyOn] = useState(true);
  useEffect(() => {
    axios.get(`${API_URL}/api/stream/notify`).then((r) => setNotifyOn(!!r.data.enabled)).catch(() => {});
  }, []);
  const toggleNotify = async () => {
    const next = !notifyOn;
    setNotifyOn(next);
    try { await axios.post(`${API_URL}/api/stream/notify`, { enabled: next }); } catch (e) { setNotifyOn(!next); }
  };
  useEffect(() => {
    let stop = false;
    const tick = async () => {
      if (stop || document.hidden) return;
      try { const r = await axios.get(`${API_URL}/api/stream/alerts`); if (!stop) setAlerts(r.data || []); } catch (e) {}
    };
    tick();
    const iv = setInterval(tick, 15000);
    return () => { stop = true; clearInterval(iv); };
  }, []);
  const applyRanks = (data, scanId) => {
    const m = {}, info = {};
    (data && data.watchlist || []).forEach((p) => { if (p.symbol && p.rank) { m[p.symbol] = p.rank; info[p.symbol] = p; } });
    setAtmRanks(m); setAtmInfo(info);
    try { if (scanId) localStorage.setItem('mf_atm_' + scanId, JSON.stringify({ watchlist: data.watchlist || [], at: Date.now() })); } catch (e) {}
  };
  const analyzeTopMovers = async (force = false) => {
    if (!selectedScanId) return;
    setAtmLoading(true);
    try {
      const r = await axios.post(`${API_URL}/api/analyze-top-movers`, { scanId: selectedScanId, force, liveMovers: (scanData && scanData.top_movers) || undefined });
      setAtmData(r.data);
      applyRanks(r.data, selectedScanId);
    } catch (e) {
      setAtmData({ error: (e.response && e.response.data && e.response.data.error) || e.message });
    } finally { setAtmLoading(false); }
  };
  const [scanData, setScanData] = useState(null);
  useEffect(() => {
    if (!selectedScanId) { setAtmRanks({}); return; }
    try {
      const raw = localStorage.getItem('mf_atm_' + selectedScanId);
      if (raw) { const j = JSON.parse(raw); const m = {}, info = {}; (j.watchlist || []).forEach((p) => { if (p.symbol && p.rank) { m[p.symbol] = p.rank; info[p.symbol] = p; } }); setAtmRanks(m); setAtmInfo(info); }
      else { setAtmRanks({}); setAtmInfo({}); }
    } catch (e) { setAtmRanks({}); setAtmInfo({}); }
    // also pull the server-side cache so ranks follow the scan across devices (phone included)
    let dead = false;
    axios.post(`${API_URL}/api/analyze-top-movers`, { scanId: selectedScanId, cacheOnly: true })
      .then((r) => {
        if (dead || !r.data || !(r.data.watchlist || []).length) return;
        applyRanks(r.data, selectedScanId);
      }).catch(() => {});
    return () => { dead = true; };
  }, [selectedScanId]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);
  const [cat, setCat] = useState('small_cap_gappers');
  const [expanded, setExpanded] = useState({});
  const [spinning, setSpinning] = useState(false);
  const [mob, setMob] = useState({ setups: false, scan: false, cats: false, charts: false, table: true });
  const mobHead = (key, label, accent = T.indigoLight, cls = '') => {
    const open = mob[key];
    const ac = accent || T.indigoLight;
    return (
      <button className={`mobhead ${cls}`} onClick={() => setMob((m) => ({ ...m, [key]: !m[key] }))} style={{
        background: open ? `${ac}1c` : T.surface1,
        border: `1px solid ${open ? `${ac}66` : T.border2}`,
        borderRadius: 12, padding: '13px 15px', fontSize: 13, fontWeight: 700,
        color: open ? ac : T.textMid, marginBottom: 10,
        boxShadow: open ? `0 0 18px -8px ${ac}` : 'none',
        transition: 'background .18s ease, border-color .18s ease, color .18s ease, box-shadow .18s ease',
      }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9 }}>
          <span style={{
            width: 7, height: 7, borderRadius: '50%', background: ac, flexShrink: 0,
            opacity: open ? 1 : 0.4, boxShadow: open ? `0 0 8px ${ac}` : 'none',
            transition: 'opacity .18s ease, box-shadow .18s ease',
          }} />
          {label}
        </span>
        <span style={{
          color: open ? ac : T.textFaint, fontSize: 12, display: 'inline-block',
          transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .18s ease, color .18s ease',
        }}>▸</span>
      </button>
    );
  };
  useEffect(() => {
    if (document.getElementById('pm-click-style')) return;
    const el = document.createElement('style');
    el.id = 'pm-click-style';
    el.textContent = [
      '.pm-click{text-decoration:none;text-underline-offset:3px;cursor:pointer}',
      '.pm-click:hover{text-decoration:underline}',
      '.mobhead{display:none;width:100%;align-items:center;justify-content:space-between;cursor:pointer;font-family:inherit}',
      '@media (max-width: 860px){',
      // watchlist-first reordering (spec: MOBILE-PREMARKET.md)
      '  .pm-main{display:flex !important;flex-direction:column;align-items:stretch !important}',
      '  .pm-main>*{min-width:0;width:100%}',
      '  .pm-side{order:2}',
      '  .pm-content{display:flex !important;flex-direction:column;min-width:0;gap:16px}',
      '  .pm-content>*{min-width:0;max-width:100%}',
      '  .pm-table{order:-1}',
      '  .pm-thead{order:-2}',
      // accordions
      '  .mobhead{display:flex !important}',
      '  .mcollapsible.mclosed{display:none !important}',
      '  .mob-hide{display:none !important}',
      // grids
      '  .pm-hero{grid-template-columns:1fr !important}',
      '  .pm-charts{grid-template-columns:1fr !important}',
      '  .pm-expand{grid-template-columns:1fr !important}',
      '  .pm-title{font-size:22px !important}',
      // side dock -> full-width overlay
      '  .pm-dock{left:0 !important;width:auto !important;max-width:100% !important;top:60px !important}',
      '}',
    ].join('');
    document.head.appendChild(el);
  }, []);
  const [dockSym, setDockSym] = useState(null);
  const [dockPanels, setDockPanels] = useState({ chart: false, news: false, details: false });
  const openDock = (sym, panel) => {
    setDockPanels((p) => (dockSym === sym ? { ...p, [panel]: true } : { chart: false, news: false, details: false, [panel]: true }));
    setDockSym(sym);
  };
  const setNewsSym = (sym) => (sym == null ? setDockPanels((p) => ({ ...p, news: false })) : openDock(sym, 'news'));
  const setChartSym = (sym) => (sym == null ? setDockPanels((p) => ({ ...p, chart: false })) : openDock(sym, 'chart'));
  useEffect(() => {
    if (!scanData || !scanData.top_movers) return undefined;
    let stop = false;
    const syms = Array.from(new Set(Object.values(scanData.top_movers).flat().map((t) => t.symbol)));
    if (!syms.length) return undefined;
    const tick = async () => {
      if (stop || document.hidden) return;
      try {
        const r = await axios.post(`${API_URL}/api/current-prices`, { symbols: syms });
        const m = r.data || {};
        if (stop || !Object.keys(m).length) return;
        setScanData((prev) => {
          if (!prev || !prev.top_movers) return prev;
          const tm = {};
          Object.entries(prev.top_movers).forEach(([k, list]) => {
            tm[k] = list.map((t) => {
              const q = m[t.symbol];
              if (!q || !q.last_price) return t;
              return { ...t, price: q.last_price,
                open_price: q.open_price > 0 ? q.open_price : t.open_price,
                volume: q.volume > 0 ? q.volume : t.volume };
            });
          });
          return { ...prev, top_movers: tm, live_asof: new Date().toTimeString().slice(0, 8) };
        });
      } catch (e) {}
    };
    const iv = setInterval(tick, 15000);
    tick();
    return () => { stop = true; clearInterval(iv); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scanData && scanData.timestamp]);

  const [sortCol, setSortCol] = useState('gap_pct');
  const [sortDir, setSortDir] = useState('desc');
  const colVal = (t, key) => {
    switch (key) {
      case '__rank': return atmRanks[t.symbol] || 9999;
      case '__stream': return streamSet.has(t.symbol) ? 0 : 1;
      case 'symbol': return t.symbol || '';
      case 'open_price': return t.open_price || 0;
      case 'price': return t.price || 0;
      case 'gap_pct': return parseFloat(t.gap_pct || 0);
      case 'open_pct': return (t.open_price > 0 && t.price > 0) ? (t.price / t.open_price - 1) * 100 : -1e9;
      case 'volume': return t.volume || 0;
      case 'rel_vol': return (t.avg_volume > 0) ? (t.volume || 0) / t.avg_volume : -1;
      case 'atr': return t.atr != null ? parseFloat(t.atr) : -1;
      case 'rsi': return t.rsi != null ? parseFloat(t.rsi) : -1;
      case 'spread': return (t.bid != null && t.ask != null && (t.price || 0) > 0 && t.ask >= t.bid) ? (t.ask - t.bid) / t.price * 100 : 1e9;
      case 'market_cap': return t.market_cap || 0;
      case 'float_shares': return t.float_shares || 0;
      case 'has_news': {
        // three tiers: fresh 48h (by article count) > older news (newer first) > none
        if (t.has_news) return 2000000 + (t.news_count || 1);
        const age = t.news_age_hours;
        if (age != null && age <= 24 * 45) return 1000000 - age;
        return 0;
      }
      default: return 0;
    }
  };
  const clickSort = (key) => {
    if (sortCol === key) setSortDir((d) => (d === 'desc' ? 'asc' : 'desc'));
    else { setSortCol(key); setSortDir((key === 'symbol' || key === '__rank') ? 'asc' : 'desc'); }
  };
  const exportCsv = (rows, cat) => {
    const cols = ['symbol', 'open_price', 'price', 'gap_pct', 'volume', 'avg_volume', 'atr', 'rsi', 'bid', 'ask', 'market_cap', 'float_shares', 'has_news', 'latest_headline'];
    const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const csv = [cols.join(',')].concat(rows.map((r) => cols.map((c) => esc(r[c])).join(','))).join('\n');
    const a = document.createElement('a');
    a.href = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
    a.download = `premarket_${cat}_${(scanData.timestamp || '').slice(0, 10)}.csv`;
    a.click();
  };

  // Load scans list
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/scans`);
        const arr = (r.data || []).sort((a, b) => (b.timestamp || '').localeCompare(a.timestamp || ''));
        setScans(arr);
        if (arr.length) setSelectedScanId(arr[0].id);
      } catch (e) { setErr(e.message); }
    })();
  }, []);

  // Load scan detail
  useEffect(() => {
    if (!selectedScanId) return;
    setLoading(true);
    (async () => {
      try {
        const r = await axios.get(`${API_URL}/api/scans/${encodeURIComponent(selectedScanId)}`);
        setScanData(r.data);
        setErr(null);
      } catch (e) { setErr(e.message); }
      finally { setLoading(false); }
    })();
  }, [selectedScanId]);

  const refresh = async () => {
    if (!selectedScanId) return;
    setSpinning(true);
    try {
      const r = await axios.get(`${API_URL}/api/scans/${encodeURIComponent(selectedScanId)}?_=${Date.now()}`);
      setScanData(r.data);
    } catch (e) {}
    setTimeout(() => setSpinning(false), 600);
  };

  const movers = scanData?.top_movers || {};
  const allTickers = useMemo(() => {
    const arr = [];
    Object.entries(movers).forEach(([k, v]) => {
      if (Array.isArray(v)) v.forEach((t) => arr.push({ ...t, cat: k, _cross: sourcesFor(sources, t.symbol) }));
    });
    return arr;
  }, [movers, sources]);

  const heroSetups = useMemo(() => {
    // dedupe by symbol (a ticker can sit in multiple categories)
    const bySym = {};
    for (const t of allTickers) {
      const sym = t.symbol;
      if (!sym) continue;
      if (!bySym[sym] || (t.volume || 0) > (bySym[sym].volume || 0)) bySym[sym] = t;
    }
    const uniq = Object.values(bySym);
    // If the AI watchlist has ranked names, those ARE the conviction setups (rank order)
    const ranked = uniq.filter((t) => atmRanks[t.symbol]).sort((a, b) => atmRanks[a.symbol] - atmRanks[b.symbol]);
    if (ranked.length) return ranked.slice(0, 4).map((t) => ({ ...t, _rank: atmRanks[t.symbol] }));
    // fallback: abs gap * relVol score with news bonus
    return uniq
      .map((t) => {
        const gap = Math.abs(parseFloat(t.gap_pct || 0));
        const relVol = (t.avg_volume || 0) > 0 ? (t.volume || 0) / t.avg_volume : 1;
        const newsBonus = t.has_news ? 1.4 : 1;
        return { ...t, score: gap * Math.min(relVol, 5) * newsBonus };
      })
      .sort((a, b) => b.score - a.score)
      .slice(0, 4);
  }, [allTickers, atmRanks]);

  const aiRankedTickers = useMemo(() => {
    const bySym = {};
    for (const t of allTickers) {
      const sym = t.symbol;
      if (!sym || !atmRanks[sym]) continue;
      if (!bySym[sym] || (t.volume || 0) > (bySym[sym].volume || 0)) bySym[sym] = t;
    }
    return Object.values(bySym).sort((a, b) => atmRanks[a.symbol] - atmRanks[b.symbol]);
  }, [allTickers, atmRanks]);

  const categoryTickers = cat === 'ai_ranked' ? aiRankedTickers : (movers[cat] || []);

  const categorySpec = [
    ...(Object.keys(atmRanks).length ? [{ key: 'ai_ranked', icon: '🎯', label: 'AI Ranked' }] : []),
    { key: 'small_cap_gappers', icon: '🚀', label: 'Small Cap Gappers' },
    { key: 'large_cap_gappers', icon: '🏛️', label: 'Large Cap Gappers' },
    { key: 'gap_downs', icon: '📉', label: 'Gap Downs' },
    { key: 'low_float_runners', icon: '💨', label: 'Low Float' },
    { key: 'penny_stock_movers', icon: '🪙', label: 'Penny Stocks' },
  ];

  if (err && !scanData) return <div style={{ padding: 60, textAlign: 'center', color: T.red }}>{err}</div>;
  if (loading && !scanData) return <div style={{ padding: 60, textAlign: 'center', color: T.textMuted }}>Loading scans…</div>;
  if (!scanData) return null;

  const totalScanned = scanData.total_scanned ?? allTickers.length;

  return (
    <div style={{ animation: 'risein .35s ease' }}>
      {/* Title row */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 16, marginBottom: 20 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <h1 className="pm-title" style={{ margin: 0, fontSize: 30, fontWeight: 800, letterSpacing: '-.02em' }}>📊 Pre-Market Scanner</h1>
            <span style={{
              padding: '4px 10px', borderRadius: 8,
              background: 'rgba(127,176,255,.12)', border: '1px solid rgba(127,176,255,.32)',
              color: T.blue, fontSize: 11, fontWeight: 800, letterSpacing: '.04em',
            }}>DAY TRADE · SCALP</span>
          </div>
          <p style={{ margin: '8px 0 0', color: T.textMuted, fontSize: 13 }}>
            Real scan data · {scanData.timestamp?.slice(0, 19) || 'unknown'} · {scans.length} scans available
          </p>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <div>
            <div style={{ fontSize: 10, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700 }}>Total scanned</div>
            <div style={{ fontFamily: T.mono, fontSize: 17, fontWeight: 700, color: T.textHi, marginTop: 2 }}>{totalScanned}</div>
          </div>
          <button onClick={openStreamModal} style={{
            padding: '9px 16px', borderRadius: 10,
            border: `1px solid ${streamStatus.running ? 'rgba(248,113,113,.5)' : T.border2}`,
            background: streamStatus.running ? 'rgba(248,113,113,.12)' : T.surface1,
            color: streamStatus.running ? T.red : T.textMid, fontSize: 13, fontWeight: 800,
            cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 7,
          }}>
            {streamStatus.running
              ? <><span style={{ width: 8, height: 8, borderRadius: '50%', background: T.red, boxShadow: `0 0 6px ${T.red}`, animation: 'spin 1.4s linear infinite' }} /> Streaming {(streamStatus.tickers || []).length}</>
              : <>📡 Stream</>}
          </button>
          <button onClick={() => analyzeTopMovers(false)} disabled={atmLoading} style={{
            padding: '9px 16px', borderRadius: 10, border: '1px solid rgba(251,191,36,.4)',
            background: 'linear-gradient(180deg,#221c11,#181208)', color: T.gold, fontSize: 13, fontWeight: 800,
            cursor: atmLoading ? 'wait' : 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 7,
          }}>
            {atmLoading ? '⏳ Analyzing…' : '🎯 Analyze Top Movers'}
          </button>
          <button onClick={refresh} style={{
            padding: '9px 16px', borderRadius: 10, border: `1px solid ${T.border2}`,
            background: T.surface1, color: T.indigoLight, fontSize: 13, fontWeight: 700,
            cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 7,
          }}>
            <span style={{ display: 'inline-block', animation: spinning ? 'spin .9s linear' : 'none' }}>🔄</span>
            Refresh
          </button>
        </div>
      </div>

      {/* Highest-conviction setups */}
      <div style={{ marginBottom: 24 }}>
        {mobHead('setups', heroSetups.some((t) => t._rank) ? '⭐ AI conviction picks' : '⚡ Highest-conviction setups', T.gold)}
        <div className="mob-hide" style={{
          fontSize: 11, fontWeight: 700, color: T.gold,
          letterSpacing: '.06em', textTransform: 'uppercase', marginBottom: 12,
        }}>{heroSetups.some((t) => t._rank) ? '⭐ AI conviction picks — top of the watchlist' : '⚡ Highest-conviction setups'}</div>
        <div className={`pm-hero mcollapsible ${mob.setups ? '' : 'mclosed'}`} style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12 }}>
          {heroSetups.map((t, i) => (
            <PreMarketHero key={t.symbol} ticker={t} category={t.cat} rank={t._rank} isStrong={t._rank ? t._rank <= 3 : (t.has_news && Math.abs(parseFloat(t.gap_pct || 0)) >= 5)} />
          ))}
        </div>
      </div>

      {/* Main grid: sidebar + content */}
      <div className="pm-main" style={{ display: 'grid', gridTemplateColumns: '248px minmax(0, 1fr)', gap: 16 }}>
        {/* Sidebar */}
        <div className="pm-side" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {mobHead('scan', '🗓️ Scan controls')}
          <div className={`mcollapsible ${mob.scan ? '' : 'mclosed'}`} style={{
            background: T.surface1, border: `1px solid ${T.border2}`, borderRadius: 16, padding: 16,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>Scan controls</div>
            <select value={selectedScanId || ''} onChange={(e) => setSelectedScanId(e.target.value)} style={{
              width: '100%', padding: '8px 10px', borderRadius: 9,
              background: T.surface2, border: `1px solid ${T.border2}`, color: T.textMid,
              fontSize: 12, fontFamily: 'inherit', marginBottom: 10,
            }}>
              {scans.slice(0, 30).map((s) => (
                <option key={s.id} value={s.id}>{(s.timestamp || s.id).slice(0, 16).replace('T', ' ')}</option>
              ))}
            </select>
            <div style={{ fontSize: 11, color: T.textMuted, marginTop: 4 }}>
              {scans.length} scans · most-recent {scans[0]?.timestamp?.slice(0, 10) || '—'}
            </div>
          </div>
          {(streamStatus.running || alerts.length > 0) && (
            <div style={{ background: T.surface1, border: '1px solid rgba(251,191,36,.25)', borderRadius: 16, padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: T.gold, textTransform: 'uppercase', letterSpacing: '.06em' }}>⚡ Swing alerts</span>
                {streamStatus.running && <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.red, boxShadow: `0 0 6px ${T.red}` }} />}
                <button onClick={toggleNotify} title={notifyOn ? 'Phone notifications ON — click to mute for today' : 'Phone notifications MUTED today — click to re-enable'} style={{
                  background: notifyOn ? 'rgba(251,191,36,.12)' : T.surface2, border: `1px solid ${notifyOn ? 'rgba(251,191,36,.4)' : T.border2}`,
                  borderRadius: 7, padding: '2px 8px', fontSize: 13, cursor: 'pointer',
                  opacity: notifyOn ? 1 : 0.55, filter: notifyOn ? 'none' : 'grayscale(1)',
                }}>{notifyOn ? '🔔' : '🔕'}</button>
                <span style={{ marginLeft: 'auto', fontFamily: T.mono, fontSize: 10, color: T.textMuted }}>{alerts.length ? alerts.length : ''}</span>
              </div>
              <div style={{ maxHeight: 300, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
                {alerts.length === 0
                  ? <div style={{ fontSize: 11, color: T.textMuted }}>none yet — fires on dip-reversals & momentum bursts while streaming</div>
                  : alerts.slice(0, 25).map((a, i) => (
                    <div key={i} onClick={() => openDock(a.symbol, 'chart')} style={{ display: 'flex', alignItems: 'center', gap: 7, padding: '6px 8px', borderRadius: 8, background: T.surface2, border: `1px solid ${T.border1}`, cursor: 'pointer' }}>
                      <span style={{ fontSize: 12 }}>{({ dip: '▼▲', mom: '⚡', exit: '🚪', 'exit-target': '🎯', 'exit-rsi': '📉', 'exit-trail': '⚠️', 'exit-stop': '⛔' })[a.type] || '⚡'}</span>
                      <span style={{ fontFamily: T.mono, fontWeight: 800, fontSize: 12, color: T.textBright }}>{a.symbol}</span>
                      <span style={{ fontFamily: T.mono, fontSize: 10.5, color: a.type === 'dip' ? T.green : T.gold }}>${a.price}</span>
                      <span style={{ marginLeft: 'auto', fontFamily: T.mono, fontSize: 9.5, color: T.textFaint }}>{String(a.time || '').slice(11, 16)}</span>
                    </div>
                  ))}
              </div>
            </div>
          )}
          {mobHead('cats', `📂 Categories · ${categorySpec.find((c) => c.key === cat)?.label || ''}`, T.blue)}
          <div className={`mcollapsible ${mob.cats ? '' : 'mclosed'}`} style={{
            background: T.surface1, border: `1px solid ${T.border2}`, borderRadius: 16, padding: 16,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.06em', marginBottom: 12 }}>Categories</div>
            {categorySpec.map((c) => {
              const list = c.key === 'ai_ranked' ? aiRankedTickers : (movers[c.key] || []);
              const active = cat === c.key;
              return (
                <button key={c.key} onClick={() => setCat(c.key)} style={{
                  display: 'flex', alignItems: 'center', gap: 10, width: '100%', padding: '9px 10px',
                  borderRadius: 9, marginBottom: 4,
                  background: active ? 'rgba(127,176,255,.1)' : 'transparent',
                  border: `1px solid ${active ? 'rgba(127,176,255,.3)' : 'transparent'}`,
                  color: active ? T.textHi : T.textMid2,
                  fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left',
                }}>
                  <span style={{ fontSize: 14 }}>{c.icon}</span>
                  <span style={{ flex: 1 }}>{c.label}</span>
                  <span style={{
                    fontFamily: T.mono, fontSize: 11, color: list.length ? (active ? T.blue : T.textMuted) : T.textFaint,
                    padding: '2px 6px', borderRadius: 5,
                    background: active ? 'rgba(127,176,255,.15)' : T.surface2,
                  }}>{list.length}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Content */}
        <div className="pm-content" style={{ display: 'flex', flexDirection: 'column', gap: 16, minWidth: 0 }}>
          {mobHead('charts', '📊 Gap distribution & category split')}
          {/* Charts row */}
          <div className={`pm-charts mcollapsible ${mob.charts ? '' : 'mclosed'}`} style={{ display: 'grid', gridTemplateColumns: '1fr 300px', gap: 12 }}>
            <GapDistributionChart allTickers={allTickers} />
            <CategoryDonut summary={scanData.summary} total={totalScanned} />
          </div>

          {atmData && <TopMoversModal data={atmData} loading={atmLoading} onRerun={() => analyzeTopMovers(true)} onClose={() => setAtmData(null)} onOpenNews={(sym) => { setAtmData(null); openDock(sym, 'news'); }} />}
          {streamOpen && <StreamModal info={atmInfo} allTickers={allTickers} status={streamStatus} sel={streamSel} setSel={setStreamSel}
            onClose={() => setStreamOpen(false)} onStart={startStream} onStop={stopStream} onAnalyze={() => { setStreamOpen(false); analyzeTopMovers(false); }} />}
          <SideDock symbol={dockSym} row={allTickers.find((x) => x.symbol === dockSym)} panels={dockPanels}
            onClose={(k) => setDockPanels((p) => ({ ...p, [k]: false }))}
            onCloseAll={() => { setDockPanels({ chart: false, news: false, details: false }); setDockSym(null); }} />

          {/* Data table */}
          {mobHead('table', `${categorySpec.find((c) => c.key === cat)?.icon || ''} ${categorySpec.find((c) => c.key === cat)?.label || 'Watchlist'} · ${categoryTickers.length}`, T.gold, 'pm-thead')}
          <div className={`pm-table mcollapsible ${mob.table ? '' : 'mclosed'}`} style={{ background: T.surface1, border: `1px solid ${T.border2}`, borderRadius: 16, overflow: 'hidden' }}>
            <div style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 18px', borderBottom: `1px solid ${T.border1}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: T.textHi }}>
                  {categorySpec.find((c) => c.key === cat)?.icon} {categorySpec.find((c) => c.key === cat)?.label}
                </span>
                <span style={{
                  fontFamily: T.mono, fontSize: 11, color: T.textMuted,
                  padding: '2px 8px', borderRadius: 5, background: T.surface2,
                }}>{categoryTickers.length}</span>
                {scanData.live_asof && (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: T.green, fontFamily: T.mono }}>
                    <span style={{ width: 6, height: 6, borderRadius: '50%', background: T.green, boxShadow: `0 0 6px ${T.green}` }} />
                    live {scanData.live_asof}
                  </span>
                )}
              </div>
              <button onClick={() => exportCsv(categoryTickers, cat)} style={{
                padding: '7px 12px', borderRadius: 8, border: `1px solid ${T.border2}`,
                background: T.surface2, color: T.green, fontSize: 12, fontWeight: 700,
                cursor: 'pointer', fontFamily: 'inherit',
              }}>⬇ Export CSV</button>
            </div>
            {categoryTickers.length === 0 ? (
              <div style={{ padding: 50, textAlign: 'center', color: T.textMuted }}>
                🚀 No {categorySpec.find((c) => c.key === cat)?.label.toLowerCase()} matched this scan.
              </div>
            ) : (
              <div style={{ overflowX: 'auto', maxWidth: '100%', WebkitOverflowScrolling: 'touch' }}>
                <div style={{ minWidth: 1360 }}>
                  <div style={{
                    display: 'grid', gridTemplateColumns: '46px 30px 56px 72px 72px 70px 70px 96px 62px 70px 52px 64px 72px 84px 80px 1fr',
                    gap: 10, padding: '10px 16px', background: T.surface2,
                    borderBottom: `1px solid ${T.border1}`,
                    fontSize: 10.5, fontWeight: 700, color: T.textMuted2,
                    textTransform: 'uppercase', letterSpacing: '.06em',
                  }}>
                    {[
                      ['__rank', 'Rank', 'center'], ['__stream', '📡', 'center'], ['symbol', 'Symbol', 'left'], ['open_price', 'Open', 'right'], ['price', 'Last', 'right'],
                      ['gap_pct', 'Gap %', 'right'], ['open_pct', 'Open %', 'right'], ['volume', 'Volume', 'right'],
                      ['rel_vol', 'Rel Vol', 'right'], ['atr', 'ATR', 'right'], ['rsi', 'RSI', 'right'],
                      ['has_news', 'News', 'center'],
                      ['spread', 'Spread %', 'right'], ['market_cap', 'Mkt Cap', 'right'], ['float_shares', 'Float', 'right'],
                      [null, 'Signals', 'left'],
                    ].map(([key, label, align], i) => (
                      <div key={i} onClick={key ? () => clickSort(key) : undefined}
                        style={{ textAlign: align, cursor: key ? 'pointer' : 'default', userSelect: 'none', color: sortCol === key ? T.gold : undefined,
                          ...(i === 0 ? { position: 'sticky', left: 0, zIndex: 3, background: T.surface2 } : {}),
                          ...(i === 1 ? { position: 'sticky', left: 46, zIndex: 3, background: T.surface2 } : {}),
                          ...(i === 2 ? { position: 'sticky', left: 76, zIndex: 3, background: T.surface2, boxShadow: '8px 0 10px -8px rgba(0,0,0,.7)' } : {}) }}>
                        {label}{sortCol === key ? (sortDir === 'desc' ? ' ↓' : ' ↑') : ''}
                      </div>
                    ))}
                  </div>
                  {[...categoryTickers].sort((a, b) => {
                    const va = colVal(a, sortCol), vb = colVal(b, sortCol);
                    if (typeof va === 'string') return sortDir === 'desc' ? String(vb).localeCompare(String(va)) : String(va).localeCompare(String(vb));
                    return sortDir === 'desc' ? vb - va : va - vb;
                  }).map((t, i) => {
                    const gap = parseFloat(t.gap_pct || 0);
                    const vol = t.volume || 0;
                    const avgVol = t.avg_volume || 0;
                    const relVol = avgVol > 0 ? vol / avgVol : null;
                    const isExp = expanded[i];
                    const badges = badgesForTicker(t);
                    return (
                      <React.Fragment key={t.symbol + i}>
                        <div
                          onClick={() => setExpanded((e) => ({ ...e, [i]: !e[i] }))}
                          style={{
                            display: 'grid', gridTemplateColumns: '46px 30px 56px 72px 72px 70px 70px 96px 62px 70px 52px 64px 72px 84px 80px 1fr',
                            gap: 10, padding: '11px 16px',
                            borderBottom: `1px solid ${T.surface2}`,
                            background: Math.abs(gap) >= 20 ? 'rgba(251,191,36,.04)' : 'transparent',
                            cursor: 'pointer', transition: 'background .15s', alignItems: 'center', fontSize: 12.5,
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = T.surfaceInset; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = Math.abs(gap) >= 20 ? 'rgba(251,191,36,.04)' : 'transparent'; }}
                        >
                          {(() => {
                            const opn = t.open_price || 0;
                            const openPct = opn > 0 ? ((t.price || 0) / opn - 1) * 100 : null;
                            const spread = (t.bid != null && t.ask != null && t.ask > 0 && (t.price || 0) > 0 && t.ask >= t.bid)
                              ? ((t.ask - t.bid) / t.price) * 100 : null;
                            const mc = t.market_cap || 0;
                            const fmtC = (x) => x >= 1e9 ? `$${(x / 1e9).toFixed(1)}B` : x >= 1e6 ? `$${(x / 1e6).toFixed(0)}M` : x > 0 ? `$${(x / 1e3).toFixed(0)}K` : '—';
                            const fl = t.float_shares || 0;
                            const rsi = t.rsi != null ? parseFloat(t.rsi) : null;
                            return (<>
                          <div style={{ position: 'sticky', left: 0, zIndex: 2, background: T.surface1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            {atmRanks[t.symbol] ? (() => {
                              const rk = atmRanks[t.symbol];
                              const top3 = rk <= 3;
                              return (
                                <span style={{
                                  fontFamily: T.mono, fontWeight: 700, fontSize: 11,
                                  color: top3 ? T.gold : T.textMid,
                                  background: top3 ? 'rgba(251,191,36,.09)' : 'transparent',
                                  border: `1px solid ${top3 ? 'rgba(251,191,36,.28)' : T.border1}`,
                                  borderRadius: 5, padding: '1px 6px', minWidth: 18, textAlign: 'center',
                                }}>{rk}</span>
                              );
                            })() : <span style={{ color: T.textFaint, fontSize: 11 }}>·</span>}
                          </div>
                          <div style={{ position: 'sticky', left: 46, zIndex: 2, background: T.surface1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                            title={streamSet.has(t.symbol) ? 'Streaming — click to stop this ticker' : 'Not streaming'}>
                            {streamSet.has(t.symbol)
                              ? <span onClick={(e) => { e.stopPropagation(); removeStreamTicker(t.symbol); }} style={{ width: 9, height: 9, borderRadius: '50%', background: T.red, boxShadow: `0 0 6px ${T.red}`, cursor: 'pointer' }} />
                              : <span style={{ color: T.textFaint, fontSize: 11 }}>·</span>}
                          </div>
                          <div onClick={(e) => { e.stopPropagation(); openDock(t.symbol, 'details'); }} title="Stock details" className="pm-click" style={{ fontFamily: T.mono, fontWeight: 700, color: T.blue,
                            position: 'sticky', left: 76, zIndex: 2, background: T.surface1, boxShadow: '8px 0 10px -8px rgba(0,0,0,.7)', display: 'flex', alignItems: 'center' }}>{t.symbol}</div>
                          <div style={{ textAlign: 'right', fontFamily: T.mono, color: T.textMuted }}>{opn > 0 ? `$${opn.toFixed(2)}` : '—'}</div>
                          <div onClick={(e) => { e.stopPropagation(); setChartSym(t.symbol); }} className="pm-click" style={{ textAlign: 'right', fontFamily: T.mono, color: T.gold }}>${(t.price || 0).toFixed(2)}</div>
                          <div style={{ textAlign: 'right', fontFamily: T.mono, color: gap >= 0 ? T.green : T.red, fontWeight: 600 }}>
                            {gap >= 0 ? '+' : ''}{gap.toFixed(1)}%
                          </div>
                          <div style={{ textAlign: 'right', fontFamily: T.mono, color: openPct == null ? T.textFaint : openPct >= 0 ? T.green : T.red }}>
                            {openPct == null ? '—' : `${openPct >= 0 ? '+' : ''}${openPct.toFixed(1)}%`}
                          </div>
                          <div style={{ textAlign: 'right', fontFamily: T.mono, color: T.textMid }}>
                            {vol >= 1e6 ? `${(vol / 1e6).toFixed(2)}M` : vol.toLocaleString()}
                          </div>
                          <div style={{ textAlign: 'right', fontFamily: T.mono, color: relVol && relVol >= 2 ? T.gold : T.textMid }}>
                            {relVol ? `${relVol.toFixed(1)}×` : '—'}
                          </div>
                          <div style={{ textAlign: 'right', fontFamily: T.mono, color: T.textMuted }}>{t.atr != null ? `$${parseFloat(t.atr).toFixed(2)}` : '—'}</div>
                          <div style={{ textAlign: 'right', fontFamily: T.mono, color: rsi == null ? T.textFaint : rsi >= 70 ? T.red2 : rsi <= 30 ? T.green : T.textMid }}>
                            {rsi == null ? '—' : rsi.toFixed(0)}
                          </div>
                          <div style={{ display: 'flex', justifyContent: 'center' }} onClick={(e) => e.stopPropagation()}>
                            {(() => {
                              const age = t.news_age_hours;
                              const state = t.has_news ? 'fresh' : (age != null && age <= 24 * 45 ? 'old' : 'none');
                              const tip = state === 'fresh' ? `${t.news_count || 1} articles (48h)`
                                : state === 'old' ? `Last article ~${Math.round(age / 24)}d ago`
                                : 'No news found';
                              const none = state === 'none';
                              return (
                                <button title={tip} disabled={none} onClick={none ? undefined : () => openDock(t.symbol, 'news')} style={{
                                  width: 26, height: 26, borderRadius: 7,
                                  border: `1px solid ${state === 'fresh' ? 'rgba(127,176,255,.55)' : T.border2}`,
                                  background: state === 'fresh' ? 'rgba(127,176,255,.12)' : T.surface2,
                                  cursor: none ? 'default' : 'pointer', fontSize: 12, padding: 0,
                                  opacity: state === 'fresh' ? 1 : state === 'old' ? 0.55 : 0.18,
                                  filter: none ? 'grayscale(1)' : 'none',
                                }}>📰</button>
                              );
                            })()}
                          </div>
                          <div style={{ textAlign: 'right', fontFamily: T.mono, color: spread != null && spread > 2 ? T.red2 : T.textMuted }}>
                            {spread != null ? `${spread.toFixed(1)}%` : '—'}
                          </div>
                          <div style={{ textAlign: 'right', fontFamily: T.mono, color: T.textMid }}>{fmtC(mc)}</div>
                          <div style={{ textAlign: 'right', fontFamily: T.mono, color: fl > 0 && fl < 20e6 ? T.gold : T.textMuted }}>
                            {fl > 0 ? `${(fl / 1e6).toFixed(1)}M` : '—'}
                          </div>
                            </>);
                          })()}
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                            {badges.slice(0, 3).map((b, j) => (
                              (b.label || '').includes('news')
                                ? <span key={j} onClick={(e) => { e.stopPropagation(); setNewsSym(t.symbol); }} style={{ cursor: 'pointer' }}><Badge {...b} /></span>
                                : <Badge key={j} {...b} />
                            ))}
                          </div>
                        </div>
                        {isExp && (
                          <div style={{
                            padding: '14px 18px', background: T.surfaceInset,
                            borderBottom: `1px solid ${T.surface2}`,
                          }}>
                            {atmInfo[t.symbol] && (() => {
                              const a = atmInfo[t.symbol];
                              const cc = a.conviction === 'high' ? T.green : a.conviction === 'medium' ? T.gold : T.textMuted;
                              const bc = a.bias === 'long' ? T.green : a.bias === 'short' ? T.red : T.textMuted;
                              return (
                                <div style={{ marginBottom: 14, padding: '11px 13px', borderRadius: 10, background: 'rgba(251,191,36,.05)', border: '1px solid rgba(251,191,36,.2)' }}>
                                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5, flexWrap: 'wrap' }}>
                                    <span style={{ fontSize: 10, color: T.gold, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase' }}>🎯 AI analysis · rank #{a.rank}</span>
                                    {a.conviction && <span style={{ fontSize: 10, fontWeight: 800, color: cc, textTransform: 'uppercase' }}>{a.conviction}</span>}
                                    {a.bias && <span style={{ fontFamily: T.mono, fontSize: 10, fontWeight: 800, color: bc, border: `1px solid ${bc}55`, borderRadius: 5, padding: '1px 6px', textTransform: 'uppercase' }}>{a.bias}</span>}
                                  </div>
                                  {a.reason && <div style={{ fontSize: 12.5, color: T.textHi, fontWeight: 600, marginBottom: 4 }}>{a.reason}</div>}
                                  {a.explanation && <div style={{ fontSize: 12, color: T.textMid, lineHeight: 1.55 }}>{a.explanation}</div>}
                                </div>
                              );
                            })()}
                            <div className="pm-expand" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 20 }}>
                              <div>
                                <div style={{ fontSize: 10, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700, marginBottom: 6 }}>Setup details</div>
                                <div style={{ fontSize: 12, color: T.textMid, lineHeight: 1.7 }}>
                                  <div>Prev close: <span style={{ fontFamily: T.mono, color: T.textHi }}>${(t.prev_close || 0).toFixed(2)}</span></div>
                                  <div>Avg volume: <span style={{ fontFamily: T.mono, color: T.textHi }}>{avgVol >= 1e6 ? `${(avgVol / 1e6).toFixed(2)}M` : avgVol.toLocaleString()}</span></div>
                                  <div>Gap $: <span style={{ fontFamily: T.mono, color: gap >= 0 ? T.green : T.red }}>{gap >= 0 ? '+' : ''}${((t.price || 0) - (t.prev_close || 0)).toFixed(2)}</span></div>
                                  <div>52w range: <span style={{ fontFamily: T.mono, color: T.textHi }}>${(t['52wk_low'] || 0).toFixed(2)} – ${(t['52wk_high'] || 0).toFixed(2)}</span></div>
                                </div>
                              </div>
                              <div>
                                <div style={{ fontSize: 10, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700, marginBottom: 6 }}>Catalyst</div>
                                {t.has_news && t.latest_headline ? (
                                  <div style={{ fontSize: 12, color: T.textMid, lineHeight: 1.5 }}>
                                    <span onClick={(e) => { e.stopPropagation(); setNewsSym(t.symbol); }}
                                      style={{ color: T.blue, fontWeight: 700, cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: 3 }}>
                                      📰 View all {t.news_count} headline{t.news_count > 1 ? 's' : ''} →
                                    </span>
                                    <div style={{ marginTop: 6 }}>{t.latest_headline}</div>
                                  </div>
                                ) : (
                                  <div style={{ fontSize: 12, color: T.textMuted }}>
                                    No news in scan ·{' '}
                                    <span onClick={(e) => { e.stopPropagation(); setNewsSym(t.symbol); }}
                                      style={{ color: T.blue, cursor: 'pointer', textDecoration: 'underline dotted', textUnderlineOffset: 3 }}>check live →</span>
                                  </div>
                                )}
                              </div>
                              <div>
                                <div style={{ fontSize: 10, color: T.textMuted2, textTransform: 'uppercase', letterSpacing: '.05em', fontWeight: 700, marginBottom: 6 }}>All signals</div>
                                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                                  {badges.length === 0
                                    ? <span style={{ fontSize: 11, color: T.textMuted }}>No standout signals beyond gap</span>
                                    : badges.map((b, j) => <Badge key={j} {...b} />)}
                                </div>
                              </div>
                            </div>
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
